import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter,
  Eye,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  ClipboardList,
  FileSignature
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { cn, formatDate } from '@/lib/utils';
import api from '@/services/api';
import CreateAssetIssueFormModal from '../components/CreateAssetIssueFormModal';

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  employeeId: string;
}

interface AssetIssueForm {
  id: string;
  formNumber: string;
  employeeId: string;
  employee?: Employee;
  purpose: string;
  notes?: string;
  status: string;
  employeeSignedAt?: string;
  issuerSignedAt?: string;
  lineManagerSignedAt?: string;
  hodItSignedAt?: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    items: number;
  };
}

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING_MANAGER', label: 'Pending Manager' },
  { value: 'PENDING_HOD', label: 'Pending HOD' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'ISSUED', label: 'Issued' },
  { value: 'RETURNED', label: 'Returned' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'REJECTED', label: 'Rejected' },
];

function getFormStatusColor(status: string): string {
  const colors: Record<string, string> = {
    DRAFT: 'bg-gray-100 text-gray-800',
    PENDING_MANAGER: 'bg-yellow-100 text-yellow-800',
    PENDING_HOD: 'bg-orange-100 text-orange-800',
    APPROVED: 'bg-blue-100 text-blue-800',
    ISSUED: 'bg-green-100 text-green-800',
    RETURNED: 'bg-purple-100 text-purple-800',
    CANCELLED: 'bg-gray-100 text-gray-800',
    REJECTED: 'bg-red-100 text-red-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
}

function getSignatureStatus(form: AssetIssueForm): string {
  if (!form.employeeSignedAt) return 'Awaiting Employee';
  if (!form.issuerSignedAt) return 'Awaiting Issuer';
  if (form.status === 'PENDING_MANAGER' && !form.lineManagerSignedAt) return 'Awaiting Manager';
  if (form.status === 'PENDING_HOD' && !form.hodItSignedAt) return 'Awaiting HOD';
  if (form.status === 'APPROVED') return 'All Signed';
  if (form.status === 'ISSUED') return 'Issued';
  if (form.status === 'RETURNED') return 'Returned';
  return form.status;
}

export default function AssetIssueFormsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Handle action=new query parameter to open create modal
  useEffect(() => {
    if (searchParams.get('action') === 'new') {
      setShowCreateModal(true);
      // Remove action param from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('action');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Fetch forms
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['asset-issue-forms', { page, search, status }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      if (search) params.append('search', search);
      if (status) params.append('status', status);
      
      const response = await api.get(`/asset-issue-forms?${params.toString()}`);
      return response.data;
    },
  });

  const forms: AssetIssueForm[] = data?.data || [];
  const pagination = data?.pagination;

  const clearFilters = () => {
    setSearch('');
    setStatus('');
    setPage(1);
  };

  const hasActiveFilters = search || status;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <ClipboardList className="w-8 h-8" />
            Asset Issue Forms
          </h1>
          <p className="text-muted-foreground">Manage asset issuance to employees</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Form
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by form number or employee name..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <select
                className="h-10 px-3 rounded-md border border-input bg-background text-sm min-w-[180px]"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
              >
                {STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <Button 
                variant={showFilters ? "secondary" : "outline"}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                <ChevronDown className={cn("w-4 h-4 ml-2 transition-transform", showFilters && "rotate-180")} />
              </Button>
              {hasActiveFilters && (
                <Button variant="ghost" onClick={clearFilters}>
                  <X className="w-4 h-4 mr-2" />
                  Clear
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Forms Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : forms.length === 0 ? (
            <div className="text-center py-12">
              <FileSignature className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No asset issue forms found</p>
              {hasActiveFilters && (
                <Button variant="link" onClick={clearFilters} className="mt-2">
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium">Form #</th>
                    <th className="text-left p-4 font-medium">Employee</th>
                    <th className="text-left p-4 font-medium">Purpose</th>
                    <th className="text-left p-4 font-medium">Assets</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-left p-4 font-medium">Signatures</th>
                    <th className="text-left p-4 font-medium">Created</th>
                    <th className="text-right p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {forms.map((form) => (
                    <tr 
                      key={form.id} 
                      className="hover:bg-muted/50 cursor-pointer"
                      onClick={() => navigate(`/asset-issue-forms/${form.id}`)}
                    >
                      <td className="p-4">
                        <span className="font-mono text-sm font-medium text-primary">
                          {form.formNumber}
                        </span>
                      </td>
                      <td className="p-4">
                        {form.employee ? (
                          <div>
                            <p className="font-medium">
                              {form.employee.firstName} {form.employee.lastName}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {form.employee.employeeId}
                            </p>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="p-4">
                        <p className="max-w-[200px] truncate">{form.purpose || '-'}</p>
                      </td>
                      <td className="p-4">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-sm font-medium">
                          {form._count?.items || 0}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={cn("px-2 py-1 text-xs font-medium rounded-full", getFormStatusColor(form.status))}>
                          {form.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="p-4">
                        <p className="text-sm text-muted-foreground">
                          {getSignatureStatus(form)}
                        </p>
                      </td>
                      <td className="p-4">
                        <p className="text-sm">{formatDate(form.createdAt)}</p>
                      </td>
                      <td className="p-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/asset-issue-forms/${form.id}`}>
                              <Eye className="w-4 h-4" />
                            </Link>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-sm text-muted-foreground">
              Showing {((page - 1) * pagination.limit) + 1} to {Math.min(page * pagination.limit, pagination.total)} of {pagination.total} forms
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={page === pageNum ? "default" : "outline"}
                      size="sm"
                      className="w-8 h-8 p-0"
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="icon"
                disabled={page === pagination.totalPages}
                onClick={() => setPage(page + 1)}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create Modal */}
      <CreateAssetIssueFormModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false);
          refetch();
        }}
      />
    </div>
  );
}
